import axiosInstance from "utils/axios";
import { uploadAttachments, DocumentUploadFeature } from "../documentUploadService";

// Converts a "today" filter (Date, or "YYYY-MM-DD" string) into the UTC instant range for that
// calendar day *in the viewer's own browser timezone*. This is deliberately independent of the
// company's AppTimeZone and of any other viewer's timezone — someone in India filtering "today"
// gets India's midnight-to-midnight, someone in Australia gets Australia's, with no server-side
// timezone assumption involved.
const localDateToUtcRange = (sessionDate) => {
  if (Array.isArray(sessionDate)) {
    if (sessionDate.length === 0) {
      return localDateToUtcRange(new Date());
    }
    const startVal = sessionDate[0];
    const start = startVal instanceof Date
      ? new Date(startVal)
      : (typeof startVal === "string" && startVal.includes("T")
          ? new Date(startVal)
          : new Date(`${startVal}T00:00:00`));
    start.setHours(0, 0, 0, 0);

    const endVal = sessionDate[1] || startVal;
    const end = endVal instanceof Date
      ? new Date(endVal)
      : (typeof endVal === "string" && endVal.includes("T")
          ? new Date(endVal)
          : new Date(`${endVal}T00:00:00`));
    end.setHours(23, 59, 59, 999);
    
    return { fromUtc: start.toISOString(), toUtc: end.toISOString() };
  }

  const start = sessionDate instanceof Date
    ? new Date(sessionDate)
    : (typeof sessionDate === "string" && sessionDate.includes("T")
        ? new Date(sessionDate)
        : new Date(`${sessionDate}T00:00:00`));
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setHours(23, 59, 59, 999);
  return { fromUtc: start.toISOString(), toUtc: end.toISOString() };
};

const buildIncidentFormData = (request) => {
  const formData = new FormData();
  formData.append("JobId", request.JobId || "");
  formData.append("IncidentTypeId", request.IncidentTypeId || "");
  formData.append("IncidentDateTime", request.IncidentDateTime || "");
  formData.append("Location", request.Location || "");
  formData.append("ReportedBy", request.ReportedBy || "");
  formData.append("SeverityLevelId", request.SeverityLevelId || "");
  if (request.StatusId) {
    formData.append("StatusId", request.StatusId);
  }
  formData.append("Description", request.Description || "");
  formData.append("CorrectiveAction", request.CorrectiveAction || "");

  // Add witnesses
  if (request.Witnesses && request.Witnesses.length > 0) {
    request.Witnesses.forEach((witness, index) => {
      formData.append(
        `Witnesses[${index}].WitnessName`,
        witness.WitnessName || "",
      );
      if (witness.UserId) {
        formData.append(`Witnesses[${index}].UserId`, witness.UserId);
      }
      if (witness.Id) {
        formData.append(`Witnesses[${index}].Id`, witness.Id);
      }
    });
  }

  // Add attachments for creation
  if (request.Attachments && request.Attachments.length > 0) {
    request.Attachments.forEach((file) => {
      formData.append("Attachments", file);
    });
  }

  // For update specific fields:
  if (request.IncidentReportId) {
    formData.append("IncidentReportId", request.IncidentReportId);
  }
  if (request.NewAttachments && request.NewAttachments.length > 0) {
    request.NewAttachments.forEach((file) => {
      formData.append("NewAttachments", file);
    });
  }
  if (request.DeletedAttachmentIds && request.DeletedAttachmentIds.length > 0) {
    request.DeletedAttachmentIds.forEach((id, index) => {
      formData.append(`DeletedAttachmentIds[${index}]`, id);
    });
  }

  return formData;
};

const dataURLtoFile = (dataurl, filename) => {
  if (!dataurl) return null;
  const fullUrl = dataurl.startsWith("data:")
    ? dataurl
    : `data:image/png;base64,${dataurl}`;
  const arr = fullUrl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const bstr = atob(arr[1] || arr[0]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

export const generateJobQRCodes = async (jobId) => {
  try {
    // 1. Generate QR token & base64 PNG data from backend
    const genRes = await axiosInstance.post(
      `/api/WHS/GenerateJobQR/${jobId}`,
      null,
      {
        headers: {
          "x-frontend-origin": window.location.origin,
        },
      },
    );

    let genData = genRes.data?.data;
    if (typeof genData === "string") {
      try {
        genData = JSON.parse(genData);
      } catch (e) {
        console.error("Failed to parse genData json:", e);
      }
    }

    const token = genData?.token || genData?.Token;
    const qrBase64 = genData?.qrBase64 || genData?.QrBase64;
    const fileName = genData?.fileName || genData?.FileName || `whs_${token}.png`;

    if (!token || !qrBase64) {
      throw new Error(genRes.data?.message || "Failed to generate QR code data from server");
    }

    // 2. Convert base64 string to a File object
    const qrFile = dataURLtoFile(qrBase64, fileName);

    // 3. Perform standard S3 pre-upload (Initiate presigned URL -> PUT to S3 -> Confirm)
    const uploadRes = await uploadAttachments(DocumentUploadFeature.JOB_QR_CODE, [qrFile]);

    if (!uploadRes.success || !uploadRes.data || uploadRes.data.length === 0) {
      throw new Error(uploadRes.error || "Failed to upload QR code image to S3");
    }

    const s3FilePath = uploadRes.data[0].filePath;

    // 4. Save JobQR record (JobId, QRToken, QRImagePath) into database
    const saveRes = await axiosInstance.post(`/api/WHS/SaveJobQR`, {
      jobId: jobId,
      qrToken: token,
      qrImagePath: s3FilePath,
    });

    return saveRes.data;
  } catch (error) {
    console.error("Error in generateJobQRCodes S3 pre-upload:", error);
    throw error;
  }
};

export const resolveWHSToken = async (token, params = {}) => {
  try {
    const config = params && Object.keys(params).length > 0 ? { params } : {};
    const response = await axiosInstance.get(`/api/WHS/ResolveToken/${token}`, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const downloadJobQR = async (jobId) => {
  try {
    const response = await axiosInstance.get(
      `/api/WHS/DownloadJobQR/${jobId}`,
      {
        responseType: "blob",
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const generateVehicleQRCodes = async (vehicleId) => {
  try {
    const genRes = await axiosInstance.post(
      `/api/WHS/GenerateVehicleQR/${vehicleId}`,
      null,
      {
        headers: {
          "x-frontend-origin": window.location.origin,
        },
      },
    );

    let genData = genRes.data?.data;
    if (typeof genData === "string") {
      try {
        genData = JSON.parse(genData);
      } catch (e) {
        console.error("Failed to parse vehicle genData json:", e);
      }
    }

    const token = genData?.token || genData?.Token;
    const qrBase64 = genData?.qrBase64 || genData?.QrBase64;
    const fileName = genData?.fileName || genData?.FileName || `vehicle_${vehicleId}.png`;

    if (!token || !qrBase64) {
      throw new Error(genRes.data?.message || "Failed to generate vehicle QR code data from server");
    }

    const qrFile = dataURLtoFile(qrBase64, fileName);
    const uploadRes = await uploadAttachments(DocumentUploadFeature.VEHICLE_QR_CODE, [qrFile]);

    if (!uploadRes.success || !uploadRes.data || uploadRes.data.length === 0) {
      throw new Error(uploadRes.error || "Failed to upload vehicle QR code image to storage");
    }

    const s3FilePath = uploadRes.data[0].filePath;

    const saveRes = await axiosInstance.post(`/api/WHS/SaveVehicleQR`, {
      vehicleId: vehicleId,
      qrToken: token,
      qrImagePath: s3FilePath,
    });

    return {
      ...saveRes.data,
      token,
      qrBase64,
      qrImagePath: s3FilePath,
    };
  } catch (error) {
    console.error("Error in generateVehicleQRCodes S3 pre-upload:", error);
    throw error;
  }
};

export const saveVehicleQR = async (vehicleId, qrToken, qrImagePath) => {
  try {
    const response = await axiosInstance.post("/api/WHS/SaveVehicleQR", {
      vehicleId,
      qrToken,
      qrImagePath,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const downloadVehicleQR = async (vehicleId) => {
  try {
    const response = await axiosInstance.get(
      `/api/WHS/DownloadVehicleQR/${vehicleId}`,
      {
        responseType: "blob",
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const verifyUserByPhone = async (phoneNumber, companyId) => {
  try {
    const response = await axiosInstance.post("/api/WHS/VerifyUserByPhone", {
      phoneNumber,
      companyId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const resolveVehicleToken = async (token) => {
  try {
    const response = await axiosInstance.get(`/api/WHS/ResolveVehicleToken/${token}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const toggleSite = async (jobId, isActive) => {
  try {
    const response = await axiosInstance.post("/api/WHS/ToggleSite", {
      jobId,
      isActive,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getIncidentReports = async (params) => {
  try {
    const response = await axiosInstance.get("/api/WHS/IncidentReports", {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createIncidentReport = async (data, companyId = null) => {
  try {
    const rawFiles = (data.Attachments || []).filter(
      (f) => f instanceof File || f instanceof Blob || (f && f.rawFile instanceof File) || (f && f.file instanceof File)
    );
    const activeCompanyId = data.CompanyId || data.companyId || companyId || null;
    let preUploadedAttachments = [];
    if (rawFiles.length > 0) {
      const actualFiles = rawFiles.map((f) => f.rawFile || f.file || f);
      const uploadResult = await uploadAttachments(
        DocumentUploadFeature.INCIDENT_REPORT_ATTACHMENT,
        actualFiles,
        activeCompanyId
      );
      if (!uploadResult.success) {
        throw new Error(uploadResult.message || "Failed to upload incident attachments");
      }
      preUploadedAttachments = uploadResult.data;
    }

    const payload = {
      JobId: data.JobId || null,
      CompanyId: activeCompanyId,
      UserId: data.UserId || null,
      IncidentTypeId: data.IncidentTypeId || null,
      IncidentDateTime: data.IncidentDateTime || null,
      Location: data.Location?.trim() || null,
      ReportedBy: data.ReportedBy?.trim() || null,
      SeverityLevelId: data.SeverityLevelId || null,
      StatusId: data.StatusId || null,
      Description: data.Description?.trim() || null,
      CorrectiveAction: data.CorrectiveAction?.trim() || null,
      Witnesses: data.Witnesses || [],
      Attachments: preUploadedAttachments.map((att, idx) => ({
        FileName: att.fileName,
        FileKey: att.filePath,
        FilePath: att.filePath,
        FileExtension: att.fileExtension,
        FileSize: rawFiles[idx]?.size || (att.fileSizeKB ? att.fileSizeKB * 1024 : 0),
        FileSizeBytes: rawFiles[idx]?.size || (att.fileSizeKB ? att.fileSizeKB * 1024 : 0),
      })),
    };

    const response = await axiosInstance.post(
      "/api/WHS/IncidentReports",
      payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateIncidentReport = async (data) => {
  try {
    const rawNewFiles = (data.NewAttachments || []).filter(
      (f) =>
        f instanceof File ||
        f instanceof Blob ||
        (f && f.rawFile instanceof File) ||
        (f && !f.isExisting && f.file instanceof File) ||
        (f && !f.isExisting && f.name && !f.id)
    );
    let preUploadedNewFiles = [];
    if (rawNewFiles.length > 0) {
      const actualFiles = rawNewFiles.map((f) => f.rawFile || f.file || f);
      const uploadResult = await uploadAttachments(
        DocumentUploadFeature.INCIDENT_REPORT_ATTACHMENT,
        actualFiles
      );
      if (!uploadResult.success) {
        throw new Error(uploadResult.message || "Failed to upload incident attachments");
      }
      preUploadedNewFiles = uploadResult.data;
    }

    const payload = {
      IncidentReportId: data.IncidentReportId,
      JobId: data.JobId || null,
      IncidentTypeId: data.IncidentTypeId || null,
      IncidentDateTime: data.IncidentDateTime || null,
      Location: data.Location?.trim() || null,
      ReportedBy: data.ReportedBy?.trim() || null,
      SeverityLevelId: data.SeverityLevelId || null,
      StatusId: data.StatusId || null,
      Description: data.Description?.trim() || null,
      CorrectiveAction: data.CorrectiveAction?.trim() || null,
      Witnesses: data.Witnesses || [],
      DeletedAttachmentIds: data.DeletedAttachmentIds || [],
      NewAttachments: preUploadedNewFiles.map((att, idx) => ({
        FileName: att.fileName,
        FileKey: att.filePath,
        FilePath: att.filePath,
        FileExtension: att.fileExtension,
        FileSize: rawNewFiles[idx]?.size || (att.fileSizeKB ? att.fileSizeKB * 1024 : 0),
        FileSizeBytes: rawNewFiles[idx]?.size || (att.fileSizeKB ? att.fileSizeKB * 1024 : 0),
      })),
    };

    const response = await axiosInstance.post(
      "/api/WHS/UpdateIncidentReports",
      payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteIncidentReport = async (id) => {
  try {
    const response = await axiosInstance.post(`/api/WHS/IncidentReports/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getIncidentReportById = async (id) => {
  try {
    const response = await axiosInstance.get(`/api/WHS/IncidentReports/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getWHSSites = async (params) => {
  try {
    const response = await axiosInstance.get("/api/WHS/Jobs", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getIncidentStatuses = async (companyId = null) => {
  try {
    const params = companyId ? { companyId } : {};
    const response = await axiosInstance.get("/api/WHS/IncidentStatuses", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getWhsDropdownOptions = async (companyId, enumValue) => {
  try {
    const response = await axiosInstance.get("/api/WHS/DropdownOptions", {
      params: { companyId, enumValue },
      showLoader: false,
    });
    const data = response.data;
    const optionsList =
      data?.data !== undefined
        ? typeof data.data === "string"
          ? JSON.parse(data.data)
          : data.data
        : data;
    return {
      success: true,
      data: Array.isArray(optionsList) ? optionsList : [],
    };
  } catch (error) {
    console.error("Error in getWhsDropdownOptions:", error);
    return {
      success: false,
      data: [],
      error: error.message || "Failed to fetch options",
    };
  }
};

export const addWhsDropdownOption = async ({ companyId, userId, enumValue, optionName, displayOrder = 0 }) => {
  try {
    const response = await axiosInstance.post("/api/WHS/DropdownOptions", {
      companyId,
      userId,
      enum: enumValue,
      optionName: optionName?.trim(),
      displayOrder,
    });
    const result = response.data;
    return {
      success: result?.success ?? true,
      optionId: result?.optionId || result?.OptionId,
      message: result?.message || result?.Message || "Option added successfully",
    };
  } catch (error) {
    console.error("Error in addWhsDropdownOption:", error);
    return {
      success: false,
      error: error?.response?.data?.message || error.message || "Failed to add option",
    };
  }
};

export const editWhsDropdownOption = async ({ optionId, companyId, optionName, displayOrder = 0 }) => {
  try {
    const response = await axiosInstance.post(`/api/WHS/DropdownOptions/${optionId}`, {
      companyId,
      optionName: optionName?.trim(),
      displayOrder,
    });
    const result = response.data;
    return {
      success: result?.success ?? true,
      optionId: result?.optionId || result?.OptionId,
      message: result?.message || result?.Message || "Option updated successfully",
    };
  } catch (error) {
    console.error("Error in editWhsDropdownOption:", error);
    return {
      success: false,
      error: error?.response?.data?.message || error.message || "Failed to update option",
    };
  }
};

export const deleteWhsDropdownOption = async ({ optionId, companyId }) => {
  try {
    const response = await axiosInstance.post(`/api/WHS/DropdownOptions/${optionId}/Delete`, null, {
      params: { companyId },
    });
    const result = response.data;
    return {
      success: result?.success ?? true,
      message: result?.message || result?.Message || "Option deleted successfully",
    };
  } catch (error) {
    console.error("Error in deleteWhsDropdownOption:", error);
    return {
      success: false,
      error: error?.response?.data?.message || error.message || "Failed to delete option",
    };
  }
};
export const getDrivers = async () => {
  try {
    const response = await axiosInstance.get("/api/WHS/Drivers");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getFleetManagers = async () => {
  try {
    const response = await axiosInstance.get("/api/WHS/FleetManagers");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getVehicleChecklists = async (params) => {
  try {
    const response = await axiosInstance.post(
      "/api/WHS/GetVehicleChecklists",
      params,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getWorkerStatus = async (jobId, userId = null, companyId = null) => {
  const params = {};
  if (userId) params.userId = userId;
  if (companyId) params.companyId = companyId;
  const response = await axiosInstance.get(`/api/WHS/WorkerStatus/${jobId}`, { params });
  return response.data;
};

export const getVehicleChecklistById = async (id) => {
  try {
    const response = await axiosInstance.get(
      `/api/WHS/VehicleChecklists/${id}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const checkIn = async (data) => {
  try {
    const response = await axiosInstance.post("/api/WHS/CheckIn", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteVehicleChecklist = async (id) => {
  try {
    const response = await axiosInstance.post(
      `/api/WHS/DeleteVehicleChecklist/${id}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const checkOut = async (data) => {
  try {
    const response = await axiosInstance.post("/api/WHS/CheckOut", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getChecklistItems = async (companyId = null) => {
  try {
    const params = companyId ? { companyId } : {};
    const response = await axiosInstance.get("/api/WHS/ChecklistItems", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getSessionsByJob = async (
  jobId,
  pageNumber = 1,
  pageSize = 10,
  sessionDate = null,
) => {
  try {
    const params = {
      JobId: jobId,
      PageNumber: pageNumber,
      PageSize: pageSize,
    };
    if (sessionDate) {
      const { fromUtc, toUtc } = localDateToUtcRange(sessionDate);
      params.SessionDateFromUtc = fromUtc;
      params.SessionDateToUtc = toUtc;
    }
    const response = await axiosInstance.get(`/api/WHS/SessionsByJob`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createChecklistItem = async (itemName, companyId = null, userId = null) => {
    try {
        const params = {};
        if (companyId) params.companyId = companyId;
        if (userId) params.userId = userId;
        const response = await axiosInstance.post(
          '/api/WHS/ChecklistItems',
          { ItemName: itemName, CompanyId: companyId, UserId: userId },
          { params }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const createVehicleChecklist = async (checklistData, userIdParam = null) => {
  try {
    const companyId = checklistData.CompanyId || checklistData.companyId || null;
    const userId = userIdParam || (checklistData.DriverIds && checklistData.DriverIds[0]) || checklistData.UserId || checklistData.userId || null;

    const rawFiles = (checklistData.Attachments || []).filter(
      (f) =>
        f instanceof File ||
        f instanceof Blob ||
        (f && f.rawFile instanceof File) ||
        (f && f.file instanceof File),
    );
    let preUploadedAttachments = [];
    if (rawFiles.length > 0) {
      const actualFiles = rawFiles.map((f) => f.rawFile || f.file || f);
      const uploadResult = await uploadAttachments(
        DocumentUploadFeature.VEHICLE_CHECKLIST_ATTACHMENT,
        actualFiles,
        companyId,
      );
      if (!uploadResult.success) {
        throw new Error(
          uploadResult.message || "Failed to upload vehicle checklist attachments.",
        );
      }
      preUploadedAttachments = uploadResult.data;
    }

    const payload = {
      VehicleId: checklistData.VehicleId,
      CompanyId: companyId,
      DriverIds: checklistData.DriverIds || null,
      DefectsDescription: checklistData.DefectsDescription || "",
      DriverComments: checklistData.DriverComments || "",
      ItemStatuses: (checklistData.ItemStatuses || []).map((st) => ({
        ChecklistItemId: st.ChecklistItemId,
        ItemStatus: Number(st.ItemStatus),
      })),
      Attachments: preUploadedAttachments.map((att, idx) => ({
        FileName: att.fileName,
        FilePath: att.filePath,
        FileExtension: att.fileExtension,
        FileSize: rawFiles[idx]?.size || 0,
      })),
    };

    const params = {};
    if (userId) params.userId = userId;

    const response = await axiosInstance.post(
      "/api/WHS/VehicleChecklists",
      payload,
      {
        params,
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error in createVehicleChecklist:", error);
    throw error;
  }
};

export const updateVehicleChecklist = async (id, checklistData) => {
  try {
    const companyId = checklistData.CompanyId || checklistData.companyId || null;
    const rawNewFiles = (checklistData.NewAttachments || []).filter(
      (f) =>
        f instanceof File ||
        f instanceof Blob ||
        (f && f.rawFile instanceof File) ||
        (f && !f.isExisting && f.file instanceof File) ||
        (f && !f.isExisting && f.name && !f.id),
    );
    let preUploadedNewFiles = [];
    if (rawNewFiles.length > 0) {
      const actualFiles = rawNewFiles.map((f) => f.rawFile || f.file || f);
      const uploadResult = await uploadAttachments(
        DocumentUploadFeature.VEHICLE_CHECKLIST_ATTACHMENT,
        actualFiles,
        companyId,
      );
      if (!uploadResult.success) {
        throw new Error(
          uploadResult.message || "Failed to upload new vehicle checklist attachments.",
        );
      }
      preUploadedNewFiles = uploadResult.data;
    }

    const payload = {
      DriverIds: checklistData.DriverIds || null,
      DefectsDescription: checklistData.DefectsDescription || "",
      DriverComments: checklistData.DriverComments || "",
      ItemStatuses: (checklistData.ItemStatuses || []).map((st) => ({
        ChecklistItemId: st.ChecklistItemId,
        ItemStatus: Number(st.ItemStatus),
      })),
      DeletedAttachmentIds: checklistData.DeletedAttachmentIds || [],
      NewAttachments: preUploadedNewFiles.map((att, idx) => ({
        FileName: att.fileName,
        FilePath: att.filePath,
        FileExtension: att.fileExtension,
        FileSize: rawNewFiles[idx]?.size || 0,
      })),
    };

    const response = await axiosInstance.post(
      `/api/WHS/VehicleChecklists/${id}`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getVehicleChecklistOptions = async () => {
  try {
    const response = await axiosInstance.get(
      "/api/WHS/GetVehicleChecklistOptions",
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
export const getSessionsByUser = async (
  userId,
  pageNumber = 1,
  pageSize = 10,
  sessionDate = null,
  isMainSite = null,
) => {
  try {
    const params = {
      UserId: userId == 1 ? null : userId,
      PageNumber: pageNumber,
      PageSize: pageSize,
    };
    if (sessionDate) {
      const { fromUtc, toUtc } = localDateToUtcRange(sessionDate);
      params.SessionDateFromUtc = fromUtc;
      params.SessionDateToUtc = toUtc;
    }
    // null/undefined = all sites, true = Main site only, false = Work sites only.
    if (isMainSite !== null && isMainSite !== undefined) {
      params.IsMainSite = isMainSite;
    }
    const response = await axiosInstance.get(`/api/WHS/SessionsByUser`, {
      params,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

// Returns the UserIds that have at least one attendance session matching the date range /
// site-type filter -- used to narrow the Workers Log directory list down to matching workers,
// without duplicating that directory query's own company-name-resolution logic.
export const getWorkerIdsBySessionFilter = async (sessionDate = null, isMainSite = null) => {
  try {
    const params = {};
    if (sessionDate) {
      const { fromUtc, toUtc } = localDateToUtcRange(sessionDate);
      params.SessionDateFromUtc = fromUtc;
      params.SessionDateToUtc = toUtc;
    }
    if (isMainSite !== null && isMainSite !== undefined) {
      params.IsMainSite = isMainSite;
    }
    const response = await axiosInstance.get(`/api/WHS/WorkerIdsBySessionFilter`, { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getJobAttendanceReport = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/WHS/GetJobAttendanceReport", payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

